import { useEffect } from 'react'
import { useLocation } from 'react-router'

import Backdrop from '@mui/material/Backdrop'
import Box from '@mui/material/Box'
import { useTheme } from '@mui/material/styles'

import { useLayoutState } from '@providers/LayoutStateProvider'

import Menu from './Menu'
import SidebarHeader from './SidebarHeader'

export default function Sidebar() {
  const { pathname } = useLocation()
  const theme = useTheme()
  const { sidebarOpen, sidebarCollapsed, setSidebarClosed, isSmallScreen } = useLayoutState()

  // On small screens, dismiss the drawer when the user navigates.
  useEffect(() => {
    if (isSmallScreen && sidebarOpen) setSidebarClosed()
  }, [pathname]) // eslint-disable-line react-hooks/exhaustive-deps

  const sidebarContent = (
    <>
      <SidebarHeader />
      <Menu />
    </>
  )

  // Desktop: pinned, width toggles between full and mini.
  if (!isSmallScreen) {
    return (
      <Box component="nav" sx={styles.nav(sidebarCollapsed, theme)}>
        <Box sx={styles.pinnedSidebar(theme)}>{sidebarContent}</Box>
      </Box>
    )
  }

  // Small screen: backdrop + floating drawer.
  return (
    <>
      <Backdrop open={sidebarOpen} onClick={setSidebarClosed} sx={{ zIndex: theme.zIndex.drawer }} />
      <Box sx={styles.drawer(sidebarOpen, theme)}>{sidebarContent}</Box>
    </>
  )
}

const styles = {
  nav: (collapsed, theme) => ({
    width: collapsed ? theme.mixins.sidebar.miniWidth : theme.mixins.sidebar.fullWidth,
    flexShrink: 0,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflow: 'hidden',
  }),
  pinnedSidebar: (theme) => ({
    width: 'inherit',
    height: '100vh',
    position: 'fixed',
    top: 0,
    left: 0,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.background.default,
    zIndex: theme.zIndex.drawer,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflow: 'hidden',
  }),
  drawer: (open, theme) => ({
    position: 'fixed',
    left: 8,
    top: 8,
    bottom: 8,
    width: theme.mixins.sidebar.fullWidth,
    backgroundColor: theme.palette.background.default,
    borderRadius: '12px',
    border: `1px solid ${theme.palette.divider}`,
    boxShadow:
      theme.palette.mode === 'light'
        ? '0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)'
        : '0 8px 32px rgba(0,0,0,0.5), 0 2px 8px rgba(0,0,0,0.3)',
    zIndex: theme.zIndex.drawer + 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    transform: open ? 'translateX(0)' : 'translateX(calc(-100% - 16px))',
    transition: 'transform 200ms ease-in-out',
    pointerEvents: open ? 'auto' : 'none',
  }),
}
